import { uuid } from '../../../../common/uuid';
import { ObjectPropertyType } from "../ObjectPropertyType";
import { IVariableScope } from './IVariableScope';

export class VariableScope implements IVariableScope {
	private id: string = uuid();
	private previousSet: Set<VariableScope> = new Set();
	private nextSet = new Set<WeakRef<VariableScope>>();
	private dirty = true;
	private rebuilding = false;

	private currentVariables: Map<string, ObjectPropertyType> = new Map<string, ObjectPropertyType>();

	private variables: Map<string, ObjectPropertyType> = new Map<string, ObjectPropertyType>();
	private diagnostics = new Set<string>();

	addDiagnostic(message: string): void {
		this.diagnostics.add(message);
	}

	getDiagnostics(visited = new Set<VariableScope>()): string[] {
		if (visited.has(this)) return [];
		visited.add(this);
		return Array.from(new Set([
			...Array.from(this.diagnostics),
			...Array.from(this.previousSet).flatMap((scope) => scope.getDiagnostics(visited)),
		]));
	}

	constructor(v: Map<string, ObjectPropertyType> = new Map()) {
		this.currentVariables = v;
		this.rebuildVariableSnapshot();
	}

	getVariables(): Map<string, ObjectPropertyType> {
		if (this.dirty && !this.rebuilding) {
			this.rebuildVariableSnapshot();
		}
		return this.variables;
	}

	setVariable(name: string, value: ObjectPropertyType) {
		this.currentVariables.set(name, value);
		this.notifyChanged();
	}

	getVariable(name: string): ObjectPropertyType | undefined {
		return this.getVariables().get(name);
	}

	private rebuildVariableSnapshot() {
		this.rebuilding = true;
		var newVariables = new Map<string, ObjectPropertyType>();

		this.previousSet.forEach((previousScope) => {
			// Create a snapshot of the previous variables
			previousScope.getVariables().forEach((variable, key) => {
				newVariables.set(key, variable);
			});
		});
		this.currentVariables.forEach((variable, key) => {
			// Create a snapshot of the current variables
			newVariables.set(key, variable);
		});

		this.variables = newVariables;
		this.dirty = false;
		this.rebuilding = false;
	}

	private notifyChanged(visited: Set<VariableScope> = new Set()) {
		if (visited.has(this)) {
			// If this scope has already been visited, skip to avoid infinite recursion
			return;
		}

		// Mark the scope as visited
		visited.add(this);
		this.dirty = true;

		// Notify all next scopes that the current scope has changed
		this.nextSet.forEach((nextScope) => {
			const scope = nextScope.deref();
			if (scope) {
				scope.notifyChanged(visited);
			} else {
				this.nextSet.delete(nextScope);
			}
		});
	}

	setVariables(values: Iterable<[string, ObjectPropertyType]>) {
		Array.from(values).forEach(([name, value]) => this.currentVariables.set(name, value));
		this.notifyChanged();
	}

	addPrevious(previous: VariableScope) {
		if (this.previousSet.has(previous)) {
			return;
		}
		this.previousSet.add(previous);
		previous.nextSet.add(new WeakRef(this));
		this.notifyChanged();
	}

	removePrevious(previous: VariableScope) {
		this.previousSet.delete(previous);
		previous.nextSet.forEach((reference) => {
			if (!reference.deref() || reference.deref() === this) {
				previous.nextSet.delete(reference);
			}
		});
		this.notifyChanged();
	}
}
